import React, { useEffect, useState } from "react";
import { Card, Tooltip, Typography } from "@mui/material";
import AgGridTable from "../../components/table/AgGridTable";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import moment from "moment";
import { Box } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const DecommissionedAssets = () => {
  const [assetsRowData, setAssetsRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [isCommissionDialogOpen, setIsCommissionDialogOpen] = useState(false);
  const navigate = useNavigate();

  const decommissionColumnDefs: ColDef[] = [
    {
      field: "device_name",
      headerName: "Asset Name",
      filter: false,
      sortable: false,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        const criticalityColors: any = {
          critical: "firebrick",
          high: "orange",
          low: "limegreen",
          default: "goldenrod",
        };


        return (
          <Box display="flex" alignItems="center" gap={1}>
            <div
              style={{
                width: "4px",
                height: "32px",
                backgroundColor:
                  criticalityColors[params?.data?.criticality?.toLowerCase()] ||
                  "goldenrod",
                borderRadius: "2px",
                marginRight: "8px",
                cursor: "pointer",
              }}
            ></div>
            <Tooltip
              title={params.data.device_name}
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#fff",
                    color: "#000",
                    boxShadow: 1,
                    fontSize: "0.775rem",
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                {params.data.device_name}
              </Typography>
            </Tooltip>

          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    { field: "vendor", headerName: "Vendor", filter: true, minWidth: 150 },
    { field: "model", headerName: "OS", filter: true, minWidth: 140 },
    { field: "device_type", headerName: "Type", filter: true, minWidth: 100 },
    {
      field: "decommission_on",
      headerName: "Decommissioned On",
      filter: true,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.format("Do MMMM, YYYY");
      },
    },
  ];

  const fetchDecommsionedAssetsData = async () => {
    try {
      setIsLoading(true);
      // NOTE: Previously fetched via GET from END_POINTS.DECOMM_DEVICE_LIST ('securityscan/decommissioned-devices').
      // Backend removed 'decommissioned-devices' API and consolidated the logic into 'decommission-recommission' (END_POINTS.DECOMMISSION_DEVICES).
      // It is now called using POST with payload { module: "decommissioned-devices" }.
      const response = await axiosInstance.post(
        END_POINTS.DECOMMISSION_DEVICES,
        {
          module: "decommissioned-devices",
        }
      );
      if (response.status === 200) {
        const result = response.data?.data?.device_list || [];
        dataMassage(result);
        setAssetsRowData(result);
      }
    } catch (error: any) {
      console.error("Error fetching decommissioned assets data:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch decommissioned assets"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const dataMassage = (data: any) => {
    data?.forEach((element: any) => {
      element.security_score_rounded = parseInt(element.security_score)
        ? Math.floor(element.security_score)
        : element.security_score;
      element.vendor = makeFirstLetterCapital(element.vendor);
      const formattedDate = element.last_scan
        ? moment(element.last_scan).format("D, MMMM, YYYY")
        : "";
      element.last_scan_date = formattedDate;
      element.region_name = element.region_name
        ? element.region_name
        : "Unassigned";
    });
    data.sort((a: any, b: any) => {
      const dateA = a.last_scan ? new Date(a.last_scan).getTime() : 0;
      const dateB = b.last_scan ? new Date(b.last_scan).getTime() : 0;
      return dateB - dateA;
    });
    data.forEach((element: any, index: number) => {
      element.number = index + 1;
    });
  };

  const makeFirstLetterCapital = (str: any) => {
    if (str) {
      return str.charAt(0).toUpperCase() + str.substring(1);
    } else {
      return "";
    }
  };

  const handleSelectionChange = (selectedRows: any) => {
    setSelectedRows(selectedRows);
  };

  const gridOptions = {
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: false,
    },
    rowHeight: 48,
    headerHeight: 48,
    suppressCellFocus: true,
    rowStyle: { cursor: "pointer" },
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    rowSelection: "multiple" as const,
    onSelectionChanged: (event: any) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node: any) => node.data);
      setSelectedRows(selectedData);
    },
  };

  useEffect(() => {
    fetchDecommsionedAssetsData();
  }, []);

  return (

    <AgGridTable
      columnDefs={decommissionColumnDefs}
      data={assetsRowData}
      height={'calc(100vh - 78px)'}
      showSearch
      isLoading={isLoading}
      isRowSelectable={true}
      rowSelection="multiple"
      onSelectionChange={handleSelectionChange}
      gridOptions={gridOptions}
    />
  );
};

export default DecommissionedAssets;
